<script lang="ts">
	import { onMount } from 'svelte';
	let canvasSize: number = 700;
	export { canvasSize as size };
	let klazz: string = '';
	export { klazz as class };
	export let bgColor: string;
	export let solfegeString: string;
	export let solfegeFontSize: number;
	export let textString: string;
	export let textFontSize: number;
	export let lineSpacingCoefficient: number;
	let canvas: HTMLCanvasElement;

	function draw({
		bgColor,
		solfegeString,
		solfegeFontSize,
		textString,
		textFontSize,
		lineSpacingCoefficient
	}: {
		bgColor: string;
		solfegeString: string;
		solfegeFontSize: number;
		textString: string;
		textFontSize: number;
		lineSpacingCoefficient: number;
	}) {
		const ctx = canvas?.getContext('2d');
		if (ctx && typeof window !== 'undefined') {
			ctx.clearRect(0, 0, canvasSize, canvasSize);

			// draw the background color:
			ctx.fillStyle = bgColor;
			ctx.fillRect(0, 0, canvasSize, canvasSize);

			// draw the texts:
			const y = drawText({
				x: canvasSize / 2,
				y: textFontSize,
				text: textString,
				ctx,
				font: 'serif',
				fill: '#fff',
				fontSize: textFontSize
			});

			// draw the hand signs:
			drawText({
				x: canvasSize / 2,
				y: y + solfegeFontSize * 0.9 + lineSpacingCoefficient * 25,
				text: filterSolfege(solfegeString),
				ctx,
				font: 'utheory-solfege',
				fill: '#fff',
				stroke: '#fff',
				strokeWidth: 2,
				fontSize: solfegeFontSize
			});

			// draw the logo:
			const logoWidth = 140;
			const logoScale = logoWidth / logoImage.width;
			const logoHeight = logoImage.height * logoScale;
			const padding = 5;
			ctx.drawImage(
				logoImage,
				canvasSize - logoWidth - padding,
				canvasSize - logoHeight - padding,
				logoWidth,
				logoHeight
			);

			// draw the url:
			const linkFontSize = 30;
			ctx.font = `${30}px serif`;
			const linkWidth = ctx.measureText('utheory.com/meme').width;
			ctx.fillStyle = bgColor;
			ctx.fillRect(0, canvasSize - 10 - linkFontSize, linkWidth + 15, 10 + linkFontSize);
			drawText({
				x: 10,
				y: canvasSize - 10,
				text: 'utheory.com/meme',
				ctx,
				font: 'serif',
				fill: '#fff',
				fontSize: linkFontSize,
				align: 'left'
			});
		}
	}

	$: {
		draw({
			bgColor,
			solfegeString,
			solfegeFontSize,
			textString,
			textFontSize,
			lineSpacingCoefficient
		});
	}

	function drawText(options: {
		x: number;
		y: number;
		text: string;
		ctx: CanvasRenderingContext2D;
		font: string;
		fill: string;
		fontSize: number;
		stroke?: string;
		strokeWidth?: number;
		align?: CanvasRenderingContext2D['textAlign'];
	}) {
		const {
			x,
			y,
			text,
			ctx,
			font,
			fontSize,
			fill,
			align = 'center',
			stroke,
			strokeWidth = 1
		} = options;

		ctx.font = `${fontSize}px ${font}`;
		ctx.textAlign = align;
		ctx.fillStyle = fill;
		const lineSpacing = lineSpacingCoefficient * fontSize;

		let top = y;
		const texts = text.split('\n');
		texts.forEach((textLine) => {
			const metrics = ctx.measureText(textLine);
			// If it's wider, we should wrap it, preferring spaces first, than characters:
			if (metrics.width > canvasSize) {
				top = drawText({
					...options,
					y: top,
					text: splitTextForWidth(ctx, textLine).join('\n')
				});
			} else {
				ctx.fillText(textLine, x, top);
				if (stroke) {
					ctx.lineWidth = strokeWidth;
					ctx.strokeStyle = stroke;
					ctx.strokeText(textLine, x, top);
				}
			}
			const height = metrics.actualBoundingBoxDescent + metrics.actualBoundingBoxAscent;
			top += height + lineSpacing;
		});
		return top - fontSize;
	}

	function splitTextForWidth(ctx: CanvasRenderingContext2D, text: string, splitOn = ' '): string[] {
		// split text on spaces:
		const { fits, wraps } = text.split(splitOn).reduce(
			(accum, word, i, words) => {
				const testFits = (i === 0 ? [word] : [accum.fits, word]).join(splitOn);
				if (getTextWidth(ctx, testFits) > canvasSize) return accum;
				return {
					fits: testFits,
					wraps: words.slice(i + 1).join(' ')
				};
			},
			{ fits: '', wraps: text }
		);
		if (fits) return [fits, wraps];
		return splitTextForWidth(ctx, text, '');
	}

	function getTextWidth(ctx: CanvasRenderingContext2D, text: string): number {
		return ctx.measureText(text).width;
	}

	const fontCharacters = [
		'd',
		'r',
		'm',
		'f',
		's',
		'l',
		't',
		'D',
		'R',
		'M',
		'F',
		'S',
		'L',
		'T',
		'+',
		'-',
		' ',
		'\n'
	];

	function filterSolfege(str: string) {
		return str
			.split('')
			.filter((c) => fontCharacters.includes(c))
			.join('');
	}

	export function getImageDataUrl() {
		return canvas.toDataURL('image/png');
	}

	export function getFile() {
		return new Promise<File>((resolve) => {
			canvas.toBlob((blob) =>
				blob
					? resolve(new File([blob], 'utheory_meme.png', { type: 'image/png' }))
					: Promise.reject('Sorry, we were unable to save that image as a file.')
			);
		});
	}

	export function uploadImageForSharing() {
		if (!canvas) return Promise.reject('No canvas!');
		const p = new Promise<{ id: string; url: string; imageUrl: string }>((resolve, reject) => {
			canvas.toBlob(function (blob) {
				if (!blob) {
					reject('Sorry, we were not able to share save an image of your meme for sharing.');
					return;
				}
				const file = new File([blob], 'utheory_meme.png', { type: 'image/png' });

				const formData = new window.FormData();
				formData.append('file', file);

				formData.append('solfegeString', solfegeString);
				formData.append('textString', textString);
				formData.append('solfegeFontSize', `${solfegeFontSize}`);
				formData.append('textFontSize', `${textFontSize}`);
				formData.append('lineSpacingCoefficient', `${lineSpacingCoefficient}`);
				formData.append('bgColor', `${bgColor}`);

				return window
					.fetch(`${import.meta.env.VITE_API_URL}/api/memes`, {
						headers: {
							Accept: 'application/json'
						},
						credentials: 'include',
						method: 'post',
						body: formData
					})
					.then(status)
					.then(json)
					.then((result: { id: string }) => {
						resolve({
							...result,
							url: `${import.meta.env.VITE_BASE_URL}/teach/resources/memes/view/${result.id}`,
							imageUrl: `https://utheory-avatars.s3.amazonaws.com/upload/memes/${result.id}.png`
						});
						return result.id;
					})
					.catch((err) => reject(err));
			}, 'image/png');
		});
		return p;
	}

	function status(response: Response) {
		if (response.status >= 200 && response.status < 300) {
			return Promise.resolve(response);
		}
		return json(response).then((json) => {
			json.status = response.status;
			return Promise.reject(json);
		});
	}

	function json(response: Response) {
		if (response && typeof response === 'object' && response.json) {
			return response.json().catch(() => ({ message: response }));
		}
		return Promise.resolve(response);
	}

	let logoImage: HTMLImageElement;
	onMount(() => {
		if (typeof window !== 'undefined') {
			let handSignFont = new FontFace(
				'utheory-solfege',
				'url(/teach/solfege-meme-generator/utheory-solfege-0.1.3.woff)'
			);
			handSignFont.load().then(() => {
				draw({
					bgColor,
					solfegeString,
					solfegeFontSize,
					textString,
					textFontSize,
					lineSpacingCoefficient
				});
			});

			logoImage = new Image();
			logoImage.src = '/teach/t-landing/logo-utheory-navigation-green.svg';
			logoImage.addEventListener('load', () => {
				draw({
					bgColor,
					solfegeString,
					solfegeFontSize,
					textString,
					textFontSize,
					lineSpacingCoefficient
				});
			});
		}
	});
</script>

<canvas bind:this={canvas} width={canvasSize} height={canvasSize} class={klazz} />
